import { uploadProjectCoverImage } from '@/backend/uploadProjectCover'
import type { Project } from '@/modules/Projects/useProjects'
import { defineStore } from 'pinia'
import { ref, toValue, type MaybeRefOrGetter } from 'vue'

/**
 * Global store for the error/uploading state of project cover images. This is required
 * because although all projects will have a cover image URL, the URLs don't necessarily
 * point to anything. So if we get a 404 error when loading a cover image, we store
 * it here so that we know not to try to load it again.
 */
export const useProjectCoverStore = defineStore('projectCovers', () => {
  const errors = ref<Set<string>>(new Set())
  const uploading = ref<Set<string>>(new Set())

  /**
   * Uploads a file as a project cover, managing state during the process
   */
  async function uploadFileAsProjectCover(
    project: MaybeRefOrGetter<Pick<Project, 'id' | 'workspaceId'>>,
    file: File,
  ) {
    const formData = new FormData()
    formData.append('file', file)

    const { id: projectId, workspaceId } = toValue(project)
    uploading.value.add(projectId)
    try {
      await uploadProjectCoverImage({
        workspaceId,
        projectId,
        formData,
      })
      errors.value.delete(projectId)
    } finally {
      uploading.value.delete(projectId)
    }
  }

  function getCoverImageUrl(
    project: MaybeRefOrGetter<Pick<Project, 'id' | 'coverImageUrls'>>,
    quality: keyof Project['coverImageUrls'],
  ) {
    const { id, coverImageUrls } = toValue(project)
    if (errors.value.has(id)) {
      return null
    }

    return coverImageUrls[quality]
  }

  return {
    errors,
    uploading,
    uploadFileAsProjectCover,
    getCoverImageUrl,
  }
})
